/**
 * channelsClassic Page.
 */
import Category from '@shared/classes/category';
import Channel from '@shared/classes/channel';
import EpgEvent from '@shared/classes/epgEvent';
import MainTemplate from '@components/templates/main/ui/main';
import Rail from '@shared/classes/rail';
import generateChannelsRails from '@shared/utils/generic/generateChannelsRails';
import useEpg from '@shared/components/utils/hooks/useEpg';
import { EventMap } from '@shared/types/interfaces/event';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RailDisplayMode,
  RailOrderSort,
  RailOrderType,
  RailType,
} from '@shared/types/enums/rail';
import {
  selectCurrentParams,
  selectParamsStack,
  updateStack,
} from '@shared/store/slices/router.slice';

/**
 * Displays the classic channels page with the rails
 */
export default function ChannelsClassic() {
  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const currentParams = useAppSelector(selectCurrentParams);
  const stack = useAppSelector(selectParamsStack);
  const { favouriteChannels } = useAppSelector(channelsSelector);

  // State
  const [filter, setFilter] = useState<Rail>(
    (currentParams as { filter: Rail })?.filter || []
  );
  const [rails, setRails] = useState<Rail[]>(
    (currentParams as { rails: Rail[] })?.rails || []
  );

  // Epg hook
  const category = (currentParams as { category: Category })?.category;
  const channelId =
    (currentParams as { asset: EpgEvent })?.asset?.channel?.id ||
    (currentParams as { channelId: string })?.channelId;
  const channelsData = useMemo(
    () =>
      favouriteChannels?.filter(
        (ch: Channel) =>
          ch.category === category?.name ||
          category?.id === ch.category ||
          !category?.id
      ),
    [favouriteChannels, category]
  );

  const { complete, epg, onPagination } = useEpg({
    channelId,
    channelsData,
    previousEpg: (stack as unknown as { epg: EventMap }[])?.length
      ? (stack[0]?.epg as EventMap)
      : undefined,
  });

  // References
  const epgRef = useRef(epg);
  const railsRef = useRef(rails);
  const stackRef = useRef(stack);
  epgRef.current = epg;
  railsRef.current = rails;
  stackRef.current = stack;

  // Effect hooks

  useEffect(() => {
    return () => {
      if (stackRef.current.length) {
        const stackIndex = stackRef.current.length - 1;
        dispatch(
          updateStack({
            index: stackIndex,
            params: { ...stackRef.current[stackIndex], epg: epgRef.current },
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    const [filterRail] = generateChannelsRails();
    setFilter(filterRail);
    const newRails: Rail[] = [];
    channelsData?.forEach((ch: Channel) => {
      for (const [key, events] of Object.entries(epg)) {
        if (events?.length && events[0].channel?.id === ch.id) {
          const rail = Rail.constructorWithData(
            {
              section: {
                title: events[0].channel?.title,
                presentationType: RailDisplayMode.CHANNEL_GUIDE_PHOTO,
                name: key,
              },
            },
            events,
            RailType.GUIDE_CHANNEL,
            false,
            true
          );
          rail.sortRail(RailOrderType.DATE, RailOrderSort.ASCENDING);
          newRails.push(rail);
        }
      }
    });
    setRails(newRails);
  }, [channelsData, epg]);

  return (
    <MainTemplate
      flags={{
        complete: complete || (!channelsData?.length && !category),
        filter: { all: true, fixed: true },
      }}
      filter={filter}
      rails={rails}
      onPagination={onPagination}
    />
  );
}
